import type { FieldId, FilterType, ProjectWellVsp } from '@core/core/types'
import { createSelector } from '@reduxjs/toolkit'
import type { Nullable, WellId } from 'common/types'

import type { RootState } from '../store'
import { vspAdapter } from './vspSlice'

const { selectAll } = vspAdapter.getSelectors<RootState>(state => state.wellVsp)

export const selectProjectWellVspByIds = createSelector(
  [selectAll, (state, wellIds: (WellId | undefined)[]) => wellIds],
  (wellsVsp, wellIds) => {
    const wellIdsSet = new Set(wellIds)
    return wellsVsp.reduce<ProjectWellVsp[]>((found, well) => {
      if (wellIdsSet.has(well.wellId)) {
        found.push(well)
      }
      return found
    }, [])
  },
)

export const selectProjectWellVspByIdTableData = createSelector(
  [selectProjectWellVspByIds],
  wellsVsp => wellsVsp.map(convertToTableData),
)

const selectProjectWellsVspByFilters = createSelector(
  [
    selectAll,
    (_, vspFilter: FilterType) => vspFilter,
    (_, vspFilter, searchQuery: Nullable<string>) => searchQuery,
    (_, vspFilter, searchQuery, fieldId: Nullable<FieldId>) => fieldId,
  ],
  (wellsVsp, filter, searchQuery, fieldId) => {
    return wellsVsp
      .filter(wellVsp => wellVsp.fieldId === fieldId)
      .filter(({ vspList }) => {
        if (filter === 'all') {
          return true
        }

        const hasVsp = vspList.length !== 0
        const onlyWithVsp = filter === 'vsp'
        return hasVsp === onlyWithVsp
      })
      .filter(({ wellName }) => {
        if (searchQuery == null || searchQuery.length < 2) {
          return true
        }

        return wellName.toLowerCase().includes(searchQuery.toLowerCase())
      })
  },
)

export const selectProjectWellsVspTableData = createSelector(
  [selectProjectWellsVspByFilters],
  wellsVsp => wellsVsp.map(convertToTableData),
)

const convertToTableData = ({ wellId: id, wellName, vspList }: ProjectWellVsp) => {
  const activeVsp = vspList.find(vsp => vsp.isActive)
  return {
    id,
    wellName,
    projectVspId: activeVsp?.id ?? null,
    vspName: activeVsp?.vspName ?? null,
    offset: activeVsp?.offset ?? null,
    isActive: activeVsp?.isActive ?? null,
  }
}
